import React, { useState, useEffect } from 'react'
import { Container, Title, Button, Switch, Loader } from "@bsf/force-ui";
import { LoaderCircle, FileText } from 'lucide-react';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import toast, { Toaster } from 'react-hot-toast';

const Branding = (args) => {
    const [settings, setSettings] = useState({
        author: '',
        author_url: '',
        plugin_name: '',
        short_name: '',
        description: '',
        hide_branding: false,
        logo_url: '',
        internal_help_links: false,
        replace_logo: false
    });

    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Fetch branding settings on component mount
    useEffect(() => {
        const fetchBrandingSettings = () => {
            setLoading(true);

            apiFetch({
                path: '/uael/v1/branding',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': uaelSettingsData.uael_nonce_action,
                },
            })
                .then((data) => {
                    setSettings({
                        author: data.agency.author || '',
                        author_url: data.agency.author_url || '',
                        plugin_name: data.plugin.name || '',
                        short_name: data.plugin.short_name || '',
                        description: data.plugin.description || '',
                        hide_branding: ('1' === data.agency.hide_branding || 'enable' === data.agency.hide_branding) ? true : false,
                        replace_logo: 'enable' === data.replace_logo ? true : false,
                        logo_url: data.logo_url || '',
                        internal_help_links: 'enable' === data.internal_help_links ? true : false,
                    });
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching branding settings:', error);
                    toast.error(__('Error fetching settings. Please try again.', 'uael'));
                    setLoading(false);
                });
        };

        fetchBrandingSettings();
    }, []);

    if (loading) {
        return (
            <div className='h-screen w-screen flex items-center justify-center' style={{ position: 'relative' }}>
                <Loader icon={null} size="lg" className="bg-transparent relative uae-loader-icon" />
            </div>
        );
    }

    const handleChange = (name, value) => {
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSaving(true);

        const dataToSend = {
            agency: {
                author: settings.author,
                author_url: settings.author_url,
                hide_branding: settings.hide_branding ? 'enable' : 'disable',
            },
            plugin: {
                name: settings.plugin_name,
                short_name: settings.short_name,
                description: settings.description,
            },
            logo_url: settings.logo_url,
            internal_help_links: settings.internal_help_links ? 'enable' : 'disable',
            replace_logo: settings.replace_logo ? 'enable' : 'disable',
        };

        fetch('/wp-json/uael/v1/branding', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': uaelSettingsData.uael_nonce_action,
            },
            body: JSON.stringify(dataToSend),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(() => {
                toast.success(__('Settings saved successfully!', 'uael'));
                location.reload();
            })
            .catch((error) => {
                console.error('Error saving settings:', error);
                toast.error(__('Error saving settings. Please try again.', 'uael'));
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <form onSubmit={handleSubmit}>
            <div
                className='rounded-lg'
            >
                <div className='flex flex-row items-center justify-between' style={{ paddingBottom: '24px' }} >
                    <Title
                        description=""
                        icon={null}
                        iconPosition="right"
                        size="sm"
                        tag="h2"
                        title={__('White Label', 'uael')}
                    />

                    <div className='flex items-center justify-between [&_h2]:text-[#6005ff]' style={{ gap: '22px' }}>
                        <div className='flex items-center gap-1 cursor-pointer' style={{ position: 'relative' }}>
                            <FileText style={{ color: '#6005ff', size: '20px' }} />
                            <a href="https://ultimateelementor.com/docs/how-to-white-label-uael/" target="_blank" className='text-[#6005ff] text-sm font-medium no-underline' style={{ color: '#6005ff', boxShadow: 'none' }}>{__('Read Documentation', 'uael')}</a>
                        </div>
                        <Button
                            type="submit"
                            style={{ backgroundColor: '#6005ff', position: 'relative' }}
                            className="flex items-center justify-center"
                            icon={isSaving ? <LoaderCircle className="animate-spin" /> : null}
                            iconPosition="left"
                            disabled={isSaving}
                        >
                            {isSaving ? __('Saving...', 'uael') : __('Save', 'uael')}
                        </Button>
                    </div>
                </div>
                <Container
                    align="stretch"
                    className="flex flex-row"
                    containerType="flex"
                    direction="column"
                    gap="sm"
                    justify="start"
                >
                    <div>
                        <Container
                            align="stretch"
                            className="flex flex-column bg-background-primary"
                            containerType="flex"
                            direction="column"
                            style={{
                                padding: "24px",
                                borderRadius: "8px",
                            }} >

                            <Title size="sm" tag="h2" title={__('Plugins Details', 'uael')} description={__('You can change the author name and plugin details that are displayed in the WordPress backend.', 'uael')} />

                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <div className='text-base text-field-label font-semibold m-0 mb-2'>Author</div>
                                    <input
                                        label={__('Author:', 'uael')}
                                        name="author"
                                        type="text"
                                        value={settings.author}
                                        className='w-full border border-subtle px-2'
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Brainstorm Force', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />

                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <div className='text-base text-field-label font-semibold m-0 mb-2'>Author URL</div>
                                    <input
                                        label={__('Author URL:', 'uael')}
                                        name="author_url"
                                        className='w-full border border-subtle px-2'
                                        type="url"
                                        value={settings.author_url}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('https://www.brainstormforce.com', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>
                            </div>

                            <div className="flex flex-row items-center mt-1 w-full gap-3">
                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <div className='text-base text-field-label font-semibold m-0 mb-2'>Plugin Name</div>
                                    <input
                                        label={__('Plugin Name:', 'uael')}
                                        name="plugin_name"
                                        type="text"
                                        className='w-full border border-subtle px-2'
                                        value={settings.plugin_name}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('Ultimate Addons', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>

                                <Container.Item className="flex flex-col space-y-1 basis-1/2 flex-1">
                                    <div className='text-base text-field-label font-semibold m-0 mb-2'>Plugin Short Name</div>
                                    <input
                                        label={__('Plugin Short Name:', 'uael')}
                                        name="short_name"
                                        type="text"
                                        className='w-full border border-subtle px-2'
                                        value={settings.short_name}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('UAE', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',     // Removes the default box shadow
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>
                            </div>

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <div className='text-base text-field-label font-semibold m-0'>Plugin Description</div>
                                <input
                                    label={__('Plugin Description:', 'uael')}
                                    name="description"
                                    type="text"
                                    className='w-full border border-subtle'
                                    value={settings.description}
                                    onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    placeholder={__('Ultimate Addons is a premium extension for Elementor...', 'uael')}
                                    style={{
                                        height: '48px',
                                        borderColor: '#e0e0e0', // Default border color
                                        outline: 'none',       // Removes the default outline
                                        boxShadow: 'none',
                                        marginTop: '16px'     // Removes the default box shadow
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                />
                            </Container.Item>

                            <div className='border border-subtle' style={{ height: '2px', backgroundColor: '#E5E7EB', marginTop: '6px' }} />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <div className='flex flex-row items-center justify-between px-1'>
                                    <div>
                                        <Title size="xs" tag="h3" title={__('Replace or Hide Logo', 'uael')} className="mt-4" description={__('Enable this option to hide the default logo or replace it with your own logo. (Note: To hide the logo, leave the logo URL field empty.)', 'uael')} />
                                    </div>
                                    <Switch
                                        onChange={(e) => handleChange('replace_logo', e)}
                                        size='md'
                                        value={settings.replace_logo}
                                        className="uael-remove-ring"
                                    />
                                </div>
                            </Container.Item>
                            {settings.replace_logo && (
                                <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                    <div className='text-base text-field-label font-semibold m-0'>{__('Enter logo URL to replace', 'uael')}</div>
                                    <input
                                        label={__('Replace Plugin Logo URL:', 'uael')}
                                        name="logo_url"
                                        type="url"
                                        className='w-full border border-subtle'
                                        value={settings.logo_url}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                        placeholder={__('https://www.example.com/logo.svg', 'uael')}
                                        style={{
                                            height: '48px',
                                            borderColor: '#e0e0e0', // Default border color
                                            outline: 'none',       // Removes the default outline
                                            boxShadow: 'none',
                                            marginTop: '16px'     // Removes the default box shadow
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#6005FF'} // Apply focus color
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}  // Revert to default color
                                    />
                                </Container.Item>
                            )}

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <div className='flex flex-row items-center justify-between px-1'>
                                    <div>
                                        <Title size="xs" tag="h3" title={__('Hide White Label Settings', 'uael')} className="mt-4" description={__('Enabling this option will hide the White Label settings. To access it again, re-activate the plugin.', 'uael')} />
                                    </div>
                                    <Switch
                                        onChange={(e) => handleChange('hide_branding', e)}
                                        size='md'
                                        value={settings.hide_branding}
                                    />
                                </div>
                            </Container.Item>

                            <div className='border border-subtle' style={{ height: '2px', backgroundColor: '#E5E7EB', marginTop: '6px' }} />

                            <Container.Item className="flex flex-col w-full space-y-1 space-x-2">
                                <Title size="sm" tag="h2" title={__('Elementor Editor Area', 'uael')} className="mt-1" />
                                <div className='flex flex-row items-center justify-between px-1'>
                                    <Title size="xs" tag="h3" title={__('Show Help Links', 'uael')} className="mt-4" description={__('Display internal help links in widget editor area.', 'uael')} />

                                    <Switch
                                        onChange={(e) => handleChange('internal_help_links', e)}
                                        size='md'
                                        value={settings.internal_help_links}
                                    />
                                </div>
                            </Container.Item>

                        </Container>
                    </div>
                    <Toaster
                        position="top-right"
                        reverseOrder={false}
                        gutter={8}
                        containerStyle={{
                            top: 20,
                            right: 20,
                            marginTop: '80px',
                        }}
                        toastOptions={{
                            duration: 5000,
                            style: {
                                background: 'white',
                            },
                            success: {
                                duration: 3000,
                                style: {
                                    color: '',
                                },
                                iconTheme: {
                                    primary: '#6005ff',
                                    secondary: '#fff',
                                },
                            },
                        }}
                    />
                </Container>
            </div>
        </form>
    );
}

export default Branding
